import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { getDataWithComp, getAlert } from "../../../../utils/SamuiUtils";
import { SUMMARY_FIELDS, DETAIL_FIELDS } from "./Fields";

export default function usePurchaseReport({
  startDate,
  endDate,
  search,
  sortField,
  sortOrder,
  page,
  perPage,
  statusFilter,
  customerType,
  currencyFilter,
}) {
  const [rawData, setRawData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [detailInfo, setDetailInfo] = useState({ index: 0, code: "", name: "" });


  // fetch once
  useEffect(() => {
    getDataWithComp("API_0201_PO_H", "Order By AP_Code DESC")
      .then(setRawData)
      .catch((e) => getAlert("FAILED", e.message));
  }, []);

  // filter, group, search, sort, paginate...
  const byDate = useMemo(
    () =>
      rawData.filter((i) => {
        const d = moment(i.Doc_Date).format("YYYY-MM-DD");
        return d >= startDate && d <= endDate;
      }),
    [rawData, startDate, endDate]
  );

  const grouped = useMemo(() => {
    const m = {};
    byDate.forEach((item) => {
      const key = item.AP_Code;
      if (!m[key]) m[key] = { ...item };
      else
        SUMMARY_FIELDS.forEach((f) => {
          if (f.numeric) m[key][f.key] = (+m[key][f.key] || 0) + (+item[f.key] || 0);
        });
    });
    return Object.values(m);
  }, [byDate]);



  const filtered = useMemo(() => {
    let a = statusFilter
      ? grouped.filter((i) => i.DocStatus_Name === statusFilter)
      : grouped;

    // 🔍 filter ตามประเทศ
    if (customerType === "ในประเทศ") {
      a = a.filter((i) => i.Doc_No?.trim().startsWith("POL"));
    } else if (customerType === "นอกประเทศ") {
      a = a.filter((i) => i.Doc_No?.trim().startsWith("POM"));
    }

    // 💰 filter ตามสกุลเงิน
    if (currencyFilter && currencyFilter !== "ทั้งหมด") {
      a = a.filter((i) => i.Doc_Currency === currencyFilter);
    }

    // 🔎 filter ตาม search
    if (search) {
      const q = search.toLowerCase();
      a = a.filter((i) =>
        Object.values(i).some((v) => v?.toString().toLowerCase().includes(q))
      );
    }

    return a;
  }, [grouped, statusFilter, customerType, currencyFilter, search]);




  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[sortField] ?? "",
        vb = b[sortField] ?? "";
      if (!isNaN(+va) && !isNaN(+vb)) {
        return sortOrder === "asc" ? va - vb : vb - va;
      }
      return sortOrder === "asc"
        ? va.toString().localeCompare(vb)
        : vb.toString().localeCompare(va);
    });
  }, [filtered, sortField, sortOrder]);

  const total = sorted.length;
  const pages = Math.ceil(total / perPage);
  const current = useMemo(
    () => sorted.slice((page - 1) * perPage, page * perPage),
    [sorted, page, perPage]
  );

  const sums = useMemo(
    () => ({
      sumNetTotal: filtered.reduce((s, i) => s + (+i.NetTotal || 0), 0),
    }),
    [filtered]
  );

  const showDetailByIndex = useCallback(
    (idx) => {
      const cust = current[idx];
      if (!cust) return;
      const filter = [
        `AND AP_Code='${cust.AP_Code}'`,
        `AND Doc_Date >= '${startDate}'`,
        `AND Doc_Date <= '${endDate}'`,
        "ORDER BY Doc_Date",
      ].join(" ");
      getDataWithComp("API_0202_PO_D", filter)
        .then((list) => {
          setDetailData(list || []);
          setDetailInfo({ index: idx, code: cust.AP_Code, name: cust.AP_Name });
        })
        .catch((e) => getAlert("FAILED", e.message));
    },

    [current, startDate, endDate]
  );

  const currencyOptions = useMemo(() => {
    const uniqueCurrencies = new Set();
    uniqueCurrencies.add("ทั้งหมด");
    rawData.forEach((item) => {
      if (item.Doc_Currency) {
        uniqueCurrencies.add(item.Doc_Currency);
      }
    }
    );
    return Array.from(uniqueCurrencies).map((currency) => ({
      value: currency,
      label: currency,
    }));
  }
    , [rawData]);

  return { current, total, pages, sums, detailData, detailInfo, currencyOptions, showDetailByIndex };
}
